import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { session } from "../lib/session";
import { formatMetaPermissionCheck, metaAnalyticsAuthorizationUrl, metaAnalyticsMessage } from "../lib/metaAnalytics";

export function MetaAnalyticsConnection({ workspaceId }) {
  const owner = session.getEmail()?.trim().toLowerCase();
  return <ScopedMetaAnalytics key={`${owner || ""}:${workspaceId || ""}`} workspaceId={workspaceId} owner={owner} />;
}

function ScopedMetaAnalytics({ workspaceId, owner }) {
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const status = useQuery({
    queryKey: ["meta-analytics-connection", owner, workspaceId],
    queryFn: async ({ signal }) => (await api.get("/api/connections/meta/analytics/status", { params: { workspace_id: workspaceId }, signal, timeout: 10000 })).data,
    enabled: !!owner && !!workspaceId,
    retry: 1,
    staleTime: 60000,
    refetchOnWindowFocus: true,
  });
  async function connect() {
    if (!owner || !workspaceId || busy) return;
    setBusy(true); setError("");
    try {
      const response = await api.post("/api/connections/meta/start", { workspace_id: workspaceId, purpose: "analytics" }, { timeout: 15000 });
      window.location.assign(metaAnalyticsAuthorizationUrl(response.data));
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Meta read-only connection could not start.");
      setBusy(false);
    }
  }
  const connected = !status.isError && status.data?.ready === true && status.data?.status === "connected_read_only";
  const message = metaAnalyticsMessage(location.search);
  return (
    <section id="meta-analytics" aria-label="Meta read-only analytics" className="rounded-xl border bg-white p-4 space-y-3" data-testid="meta-analytics-connection">
      <h2 className="text-lg font-semibold">Meta analytics — read-only</h2>
      <p className="text-sm text-slate-600">Requests ads_read only, with Meta’s basic identity permissions where applicable. No ad management, publishing, budget changes or spending. This separate record never replaces your full Meta publishing connection below.</p>
      <p role="status">{!owner ? "Sign in to OmniMark first." : !workspaceId ? "Select a workspace first." : status.isError ? "Connection status could not be verified. No saved browser badge is trusted." : connected ? "Saved read-only access confirmed. Live campaign ingestion is not enabled yet." : status.isPending ? "Checking saved account access…" : "Read-only analytics is not connected, or needs reconnection."}</p>
      {connected && <p className="text-xs text-slate-500">Last provider permission check: {formatMetaPermissionCheck(status.data.connection?.metadata?.permissions_verified_at)}. Connection access is not proof that campaign metrics are current.</p>}
      {message && <p className="text-sm" role="status">{message}</p>}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={connect} disabled={!owner || !workspaceId || busy} className="rounded-md border px-3 py-2" data-testid="connect-meta-analytics">{busy ? "Opening Meta…" : connected ? "Reconnect read-only analytics" : "Connect Meta analytics (read-only)"}</button>
        <button type="button" onClick={() => status.refetch()} disabled={!owner || !workspaceId || status.isFetching} className="rounded-md border px-3 py-2">Refresh access status</button>
      </div>
    </section>
  );
}
